<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('projects.editBase')"></page-title>
    </div>

    <jet-form-section @submitted="updateBase()">

        <template #form>
            <!-- Card General Project Information -->
            <form-card :title="$t('projects.project')" :description="$t('projects.projectDescription')">
                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t("projects.name") }} <RequiredMark /></label>
                    <input type="text" name="name" id="name" v-model="form.updateBase.name"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors['updateBase.name']" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="number" class="block text-sm font-medium text-gray-700">{{
                            $t("projects.number")
                        }}</label>
                    <input type="text" name="number" id="number" v-model="form.updateBase.number"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['updateBase.number']" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="street" class="block text-sm font-medium text-gray-700">{{
                            $t("projects.street")
                        }} <RequiredMark /></label>
                    <input type="text" name="street" id="street" v-model="form.updateBase.location.street"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['updateBase.location.street']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-1">
                    <label for="zip" class="block text-sm font-medium text-gray-700">{{ $t("projects.zip") }} <RequiredMark /></label>
                    <input type="text" name="zip" id="zip" v-model="form.updateBase.location.zip"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300 pr-1">
                    <jet-input-error :message="form.errors['updateBase.location.zip']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("projects.city") }} <RequiredMark /></label>
                    <input type="text" name="city" id="city" v-model="form.updateBase.location.city"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['updateBase.location.city']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                    <label for="country" class="block text-sm font-medium text-gray-700">{{
                            $t("projects.country")
                        }} <RequiredMark /></label>
                    <Dropdown v-model="form.updateBase.location.country"
                              :options="countries"
                              optionValue="code"
                              optionLabel="name"
                              :placeholder="$t('forms.selectCountry')"
                              class="w-full mt-1" />
                    <jet-input-error :message="form.errors['updateBase.location.country']" class="mt-2" />
                </div>

            </form-card>

            <!-- Card Project Settings -->
            <form-card :title="$t('projects.settings')" :description="$t('projects.settingsDescription')">
                <div class="col-span-3">
                    <label for="base_fee" class="block text-sm font-medium text-gray-700">{{ $t("projects.baseFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="base_fee" id="base_fee" v-model="form.updateBase.base_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div>       
                    <jet-input-error :message="form.errors['base_fee']" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="consultancy_fee " class="block text-sm font-medium text-gray-700">{{ $t("projects.consultancyFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="consultancy_fee" id="consultancy_fee" v-model="form.updateBase.consultancy_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div> 
                    <jet-input-error :message="form.errors['consultancy_fee']" class="mt-2" />
                </div> 
            </form-card>            

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button @click="cancel" type="button" class="inline-block uppercase font-semibold text-xs tracking-widest">{{ $t('actions.cancel') }}</button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3">
                    {{ $t("actions.save") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import { countries } from "@/Shared/countries";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import FormCard from "@/Shared/Components/FormCard";
import PageTitle from "@/Shared/Components/PageTitle";
import RequiredMark from "@/Shared/Components/RequiredMark";
import { useForm } from "@inertiajs/inertia-vue3";

export default defineComponent({
    props: ["project"],
    inheritAttrs: false,
    emits: ["closeModal"],
    layout: MainLayout,
    components: {
        PageTitle,
        FormCard,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        RequiredMark
    },
    data() {
        return {
            countries,
            form: useForm({
                updateBase: {
                    name: this.project.name,
                    number: this.project.number,
                    location: {
                        street: this.project.street,
                        zip: this.project.zip,
                        city: this.project.city,
                        country: this.project.country
                    },
                    base_fee: this.project.base_fee,
                    consultancy_fee: this.project.consultancy_fee,
                }
            }),
        };
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        updateBase() {
            this.form.post(this.route("advisors.projects.update", { project: this.project.uuid }), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'projects.updated'
                })
            });
        }
    }
});
</script>

<style scoped></style>
